import React, { useEffect, useState, useCallback } from "react";
import styles from "./SandboxTiles.module.css";
import { Button } from "@/aible";
import { useSelector, useDispatch } from "types/store";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/aible/Dialog/Dialog";
import TrashPurpleIcon from "images/SenseIcons/trash-purple.svg";
import { getAPIToken } from "@/utils/request";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as accountsReducer from "@/containers/Accounts/reducer";
import { fetchAllSandboxes, getSandboxes } from "@/containers/OpenClaw/openClawSlice";
import * as viewReducer from "@/containers/View/reducer";
import { UsageStats } from "./UsageStats";
// ── Phase config ───────────────────────────────────────────────────────
const PHASE_META = {
    running: { color: "#22c55e", label: "Running", icon: "▶" },
    pending: { color: "#d97706", label: "Pending", icon: "◉" },
    succeeded: { color: "#7c5cfc", label: "Succeeded", icon: "✓" },
    failed: { color: "#dc2626", label: "Failed", icon: "✕" },
    terminated: { color: "#78748a", label: "Terminated", icon: "■" },
    unknown: { color: "#7c5cfc", label: "Unknown", icon: "?" },
    ready: { color: "#7c5cfc", label: "Succeeded", icon: "✓" }
};
function phaseMeta(phase) {
    return PHASE_META[phase.toLowerCase()] ?? PHASE_META.unknown;
}
// ── Arrow icon for the Open button ─────────────────────────────────────
const ArrowIcon = () => (React.createElement("svg", { viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M7 17L17 7M17 7H8M17 7V16", strokeLinecap: "round", strokeLinejoin: "round" })));
// ── Sandbox icon ───────────────────────────────────────────────────────
const SandboxIcon = () => (React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("rect", { x: "3", y: "7", width: "18", height: "13", rx: "2", stroke: "#fff", strokeWidth: "1.8" }),
    React.createElement("path", { d: "M8 7V5C8 3.89543 8.89543 3 10 3H14C15.1046 3 16 3.89543 16 5V7", stroke: "#fff", strokeWidth: "1.8" }),
    React.createElement("line", { x1: "3", y1: "12", x2: "21", y2: "12", stroke: "#fff", strokeWidth: "1.8" }),
    React.createElement("rect", { x: "10", y: "10", width: "4", height: "4", rx: "0.5", fill: "#fff", opacity: "0.5" })));
const SandboxTile = ({ sandbox, index, onOpen, fetchSandboxes }) => {
    const [showPolicy, setShowPolicy] = useState(false);
    const [showStats, setShowStats] = useState(false);
    const [sandboxIsLoading, setSandboxIsLoading] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const gatewayUnhealthy = sandbox?.gateway_healthy !== true;
    const meta = phaseMeta(sandbox.phase);
    const policyStr = sandbox.policy_json != null
        ? typeof sandbox.policy_json === "string"
            ? sandbox.policy_json
            : JSON.stringify(sandbox.policy_json, null, 2)
        : null;
    const showDeleteModal = (val) => {
        setShowDeleteConfirmation(val);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `${styles.tile} group`, style: {
                "--phase-color": meta.color,
                animationDelay: `${index * 60}ms`,
                cursor: gatewayUnhealthy ? undefined : "pointer"
            }, onClick: () => {
                if (gatewayUnhealthy) {
                    return;
                }
                onOpen(sandbox);
            } },
            React.createElement("div", { className: "flex flex-col w-[100%]" },
                React.createElement("div", { className: "flex justify-between w-[100%]" },
                    React.createElement("div", { className: styles.iconWrap },
                        React.createElement(SandboxIcon, null)),
                    React.createElement("div", { className: styles.content },
                        React.createElement("div", { className: styles.titleRow },
                            React.createElement("span", { className: styles.name, title: sandbox.name }, sandbox.name)),
                        React.createElement("div", { className: styles.tags },
                            React.createElement("span", { className: styles.tag },
                                React.createElement("span", { className: styles.tagDot, style: {
                                        "--phase-color": gatewayUnhealthy ? PHASE_META.pending.color : meta.color
                                    } }),
                                gatewayUnhealthy ? "Gateway unhealthy" : sandbox.phase))),
                    React.createElement("div", { className: "flex items-start" },
                        React.createElement(Button, { className: `${styles.deleteBtn} opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto`, onClick: (e) => {
                                e.stopPropagation();
                                showDeleteModal(true);
                            }, isLoading: sandboxIsLoading },
                            React.createElement("img", { src: TrashPurpleIcon, alt: "Delete sandbox" })))),
                React.createElement("div", { className: "flex flex-col w-[100%] items-center" },
                    React.createElement("div", { className: "flex gap-2" },
                        React.createElement("button", { className: styles.policyToggle, onClick: (e) => {
                                e.stopPropagation();
                                setShowStats((v) => !v);
                            } }, showStats ? "Hide stats" : "View stats")),
                    showStats && (React.createElement("div", { className: styles.statsBlock, onClick: (e) => e.stopPropagation() },
                        React.createElement("div", { className: styles.statsSection },
                            React.createElement("div", { className: styles.statsSectionHeader }, "Usage"),
                            React.createElement(UsageStats, { sandboxName: sandbox.name })))),
                    showPolicy && policyStr != null && (React.createElement("pre", { className: styles.policyBlock, onClick: (e) => e.stopPropagation() }, policyStr))))),
        showDeleteConfirmation && (React.createElement(SandboxDeleteModal, { showModal: showDeleteConfirmation, updateShowModal: setShowDeleteConfirmation, sandboxName: sandbox.name, fetchSandboxes: fetchSandboxes, setSandboxIsLoading: setSandboxIsLoading }))));
};
const SandboxDeleteModal = ({ showModal, updateShowModal, sandboxName, fetchSandboxes, setSandboxIsLoading }) => {
    const dispatch = useDispatch();
    const openshellBaseUrl = useSelector((state) => accountsReducer.getOpenShellBaseUrl(state));
    const handleDeleteSandbox = async () => {
        setSandboxIsLoading(true);
        updateShowModal(false);
        const apiToken = getAPIToken();
        if (apiToken === undefined) {
            return;
        }
        const endpoint = `${openshellBaseUrl}/sandboxes/${sandboxName}`;
        try {
            const res = await fetch(endpoint, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + apiToken }
            });
            if (!res.ok)
                throw new Error(`${res.status} ${res.statusText}`);
            await fetchSandboxes();
        }
        catch {
            console.log("Failed to delete sandbox: ", sandboxName);
        }
        finally {
            setSandboxIsLoading(false);
        }
    };
    return (React.createElement(Dialog, { open: showModal, onOpenChange: () => {
            updateShowModal(false);
        } },
        React.createElement(DialogContent, { size: "sm" },
            React.createElement(DialogTitle, null, "Delete OpenClaw Agent?"),
            React.createElement("div", { className: styles.deleteConfirmationText },
                React.createElement("span", null, "This action will permanently delete"),
                React.createElement("span", { className: "font-bold" }, ` ${sandboxName}. `),
                React.createElement("span", null, "This cannot be undone.")),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { onClick: () => updateShowModal(false) }, "Cancel"),
                React.createElement(Button, { onClick: () => handleDeleteSandbox(), className: styles.deleteBtnConfirmation }, "Delete")))));
};
const SandboxTiles = ({ pollInterval = 0, onOpen }) => {
    const sandboxes = useSelector((state) => getSandboxes(state));
    const [error, setError] = useState(null);
    const isSandboxesLoading = useSelector((state) => viewReducer.getIsSandboxesLoading(state)) ?? true;
    const dispatch = useDispatch();
    const openshellBaseUrl = useSelector((state) => accountsReducer.getOpenShellBaseUrl(state));
    const handleOpen = useCallback((sandbox) => {
        if (onOpen) {
            onOpen(sandbox);
        }
        else {
            dispatch(chatGPTReducer.initOpenshellAgent(sandbox));
        }
    }, [onOpen]);
    const fetchSandboxes = useCallback(async () => {
        const apiToken = getAPIToken();
        if (apiToken === undefined) {
            return;
        }
        try {
            await dispatch(fetchAllSandboxes());
            setError(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch sandboxes");
        }
    }, [openshellBaseUrl]);
    useEffect(() => {
        fetchSandboxes();
        if (pollInterval > 0) {
            const id = setInterval(fetchSandboxes, pollInterval);
            return () => clearInterval(id);
        }
    }, [fetchSandboxes, pollInterval]);
    return (React.createElement("div", { className: styles.wrapper },
        isSandboxesLoading && React.createElement("div", { className: styles.loadingState }, "Loading sandboxes\u2026"),
        error && !isSandboxesLoading && (React.createElement("div", { className: styles.errorState },
            React.createElement("strong", null, "Error:"),
            " ",
            error,
            React.createElement("br", null),
            React.createElement(Button, { className: styles.retryBtn, onClick: fetchSandboxes, isLoading: isSandboxesLoading }, "Retry"))),
        !isSandboxesLoading && !error && sandboxes.length === 0 && (React.createElement("div", { className: styles.emptyState }, "No sandboxes found")),
        !isSandboxesLoading &&
            !error &&
            sandboxes.map((sb, i) => (React.createElement(SandboxTile, { key: sb.name, sandbox: sb, index: i, onOpen: handleOpen, fetchSandboxes: fetchSandboxes })))));
};
export default SandboxTiles;
